// Vue
import { ref, nextTick, Ref } from "vue";

// Dependencies
import { defineStore } from "pinia";

// Config
import createWizardSteps, { CreateWizardStepType } from "@/features/BuildingProject/configuration/createWizardSteps";

import { countryInAlpha2Map, countryLongNameToEnumMap, countryCreateWizardFlows } from "./../localizationConfig";

// Types
import { TranslatedEpdType, Country_Enum } from "../types/LocalizationTypes";

// ///////////////////
// Methods for country handling (outside of store)
// ///////////////////

function validateCountry(country: string): boolean {
  return country in countryLongNameToEnumMap;
}

// ///////////////////
// Interface + Store
// ///////////////////

interface LocalizationProvider {
  activeLang: Ref<string>;
  country: Ref<Country_Enum | null>;
  // Getters
  getCountry: () => Country_Enum | null;

  // Setters
  setCountry: (newCountry: Country_Enum) => void;
  setCountryFromLongName: (newCountry: string) => void;

  // Methods for country handling
  validateCountry: (country: string) => boolean;
  getCountryInAlpha2: (country: string) => string | undefined;
  getValidCountriesList: () => string[];

  // Methods for wizard flows
  getCreateWizardFlow: () => CreateWizardStepType[];

  // Methods for translations in library epds
  getEpdTranslation: (epd: TranslatedEpdType | undefined, fallback: string) => string;
  getLanguages: () => string[];
}

const useLocalization = defineStore("LocalizationProvider", (): LocalizationProvider => {
  // ///////////////////
  // Country setup
  // ///////////////////

  // ///////////////////
  // Refs
  // ///////////////////

  const country = ref<Country_Enum | null>(null);
  const languages = ref<string[]>(navigator.languages.map((lang) => lang.split("-")[0]));
  const activeLang = ref<string>(navigator.language.split("-")[0]);

  // ///////////////////
  // Getters
  // ///////////////////

  const getCountry = (): Country_Enum | null => country.value;
  const getLanguages = (): string[] => languages.value;

  // ///////////////////
  // Setters
  // ///////////////////

  function setCountry(newCountry: Country_Enum | null) {
    country.value = newCountry;
  }

  function setCountryFromLongName(newCountry: string) {
    const countryEnum = countryLongNameToEnumMap[newCountry];
    // Set newCountry country in next tick
    nextTick(() => {
      if (countryEnum) {
        setCountry(countryEnum);
      } else {
        setCountry(null);
      }
    });
  }

  // ///////////////////
  // Methods for country handling
  // ///////////////////

  /**
   * The ISO alpha-2 code Google Places restricts a search to, from a country as the address row spells
   * it ("Denmark", "Faroe Islands").
   *
   * Two hops, and both are needed. This used to be a single lookup of
   * `countryInAlpha2Map[country.toLowerCase()]`, against a map keyed by the Country_Enum's UPPERCASE
   * values — so it returned undefined for every input that has ever been passed to it, and the only
   * caller (the Location section's `restrictToCountry`) silently searched the whole world. Case alone
   * does not fix it either: "Denmark".toUpperCase() happens to equal Country_Enum.DENMARK, but
   * "Faroe Islands".toUpperCase() is "FAROE ISLANDS" where the enum is FAROE_ISLAND. So resolve the
   * long name through the map that owns that spelling first, and fall back to treating the input as an
   * enum value for a caller that already holds one.
   *
   * Undefined for an unknown or empty country, which AddressInput reads as "do not restrict". That is
   * the safe direction: a project whose country is outside the map keeps the global search it has
   * always had rather than being restricted to nothing.
   */
  function getCountryInAlpha2(country: string): string | undefined {
    if (!country) return undefined;

    const countryEnum = countryLongNameToEnumMap[country] ?? (country.toUpperCase() as Country_Enum);

    return countryInAlpha2Map[countryEnum] ?? undefined;
  }

  function getValidCountriesList(): string[] {
    return Object.keys(Country_Enum)
      .filter((key) => isNaN(Number(key)))
      .map((key) =>
        key
          .toLowerCase()
          // Replace underscores with spaces
          .replace(/_/g, " ")
          // First letter uppercase
          .replace(/(^\w|\s\w)/g, (m) => m.toUpperCase())
      );
  }

  // ///////////////////
  // Methods for wizard flows
  // ///////////////////

  function getCreateWizardFlow(): CreateWizardStepType[] {
    const country = getCountry() ?? "DEFAULT";
    const wizardFlowIds = countryCreateWizardFlows[country];
    if (!wizardFlowIds) return [];

    const wizardFlow = [] as CreateWizardStepType[];
    const steps = createWizardSteps();
    for (const id of wizardFlowIds) {
      const createWizardStep = steps.find((step) => step.id === id);
      if (!createWizardStep) continue;
      wizardFlow.push(createWizardStep);
    }
    return wizardFlow;
  }

  // ///////////////////
  // Methods for translations in library epds
  // ///////////////////

  function getEpdTranslation(epd: TranslatedEpdType | undefined, fallback: string): string {
    if (!epd) return fallback;

    // Return first of possible translations based on languages
    for (let lang of languages.value) {
      // Map "en-US" to "en"
      lang = lang === "en-US" ? "en" : lang;

      if (epd[lang]) return epd[lang].translation;
    }

    return fallback;
  }

  // ///////////////////
  // Return
  // ///////////////////

  return {
    activeLang,
    country,
    // Getters
    getCountry,
    getLanguages,

    // Setters
    setCountry,
    setCountryFromLongName,
    getValidCountriesList,

    // Methods for country handling
    validateCountry,
    getCountryInAlpha2,

    // Methods for wizard flows
    getCreateWizardFlow,

    // Methods for translations in library epds
    getEpdTranslation,
  };
});

export default useLocalization;
export { validateCountry };
